import React, { useRef, useEffect, useState } from 'react';
import { Award, Shield, Wrench, CheckCircle, Users, Network } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { whyChooseUs } from '../data/mock';

const iconMap = {
  Award: Award,
  Shield: Shield,
  Wrench: Wrench,
  CheckCircle: CheckCircle,
  Users: Users,
  Network: Network
};

const WhyChooseSection = () => {
  const [visibleCards, setVisibleCards] = useState(new Set());
  const observerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = entry.target.dataset.cardId;
            setVisibleCards((prev) => new Set([...prev, cardId]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observe all card refs
    cardRefs.current.forEach((card) => {
      if (card && observerRef.current) {
        observerRef.current.observe(card);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <section className="py-24 bg-zinc-50 relative overflow-hidden">
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-zinc-50 to-white"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-3">
            <span className="text-yellow-600 text-xs font-semibold tracking-widest uppercase">Why Choose Us</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-black mb-5 leading-tight">
            Built for <span className="text-yellow-500">Excellence</span>
          </h2>
          <p className="text-zinc-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Trusted by fabricators, workshops, and industrial facilities across India for uncompromising quality
          </p>
        </div>

        {/* Features Grid - Minimal Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyChooseUs.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            const isVisible = visibleCards.has(`card-${index}`);
            
            return (
              <div
                key={index}
                data-card-id={`card-${index}`}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                className={`transform transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'
                }`}
              >
                <Card 
                  className="bg-white border border-zinc-200 hover:border-yellow-500 hover:shadow-xl transition-all duration-500 h-full"
                >
                  <CardContent className="p-8">
                    <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-black mb-3">
                      {item.title}
                    </h3>
                    <p className="text-zinc-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA - Cleaner */}
        <div className="mt-20 text-center">
          <div className="inline-block bg-white border border-zinc-200 rounded-xl p-10 max-w-3xl shadow-sm">
            <h3 className="text-2xl font-semibold text-black mb-4">
              Experience the RK ROTOARC Difference
            </h3>
            <p className="text-zinc-600 text-base mb-8 leading-relaxed">
              Join hundreds of satisfied customers who trust our equipment for their critical welding operations
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-sm text-zinc-700">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">ISI Certified Products</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">Pan-India Distribution</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-yellow-500" />
                <span className="font-medium">Technical Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
